import { Download, Calendar, ShieldCheck, TrendingUp, Star, ArrowRight, Sparkles, CheckCircle2, Award } from "lucide-react";

export default function Hero() {
  const scrollToSection = (sectionId) => {
    setTimeout(() => {
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }, 100);
  };

  return (
    <section id="home" className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#070E1B] text-white py-16 lg:py-24">
      {/* ── Background Mesh Ambient Lighting & Dynamic Grid ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {/* Gold Light Flare Left */}
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#C9A84C]/15 rounded-full blur-[140px] animate-pulse-glow" />
        {/* Navy/Blue Light Flare Right */}
        <div className="absolute top-1/3 -right-32 w-[600px] h-[600px] bg-[#3D6ACC]/20 rounded-full blur-[160px] animate-pulse-glow" />
        {/* Center Subtly Warm Flare */}
        <div className="absolute -bottom-40 left-1/3 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[140px]" />

        {/* SVG Subtle Pattern Grid Overlay */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">

          {/* ══ LEFT COLUMN: High-Impact Messaging & Trust ════════════════════ */}
          <div className="lg:col-span-7 space-y-8 animate-fade-in-up">

            {/* Live Eyebrow Badge */}
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#162236]/90 border border-[#C9A84C]/30 shadow-inner backdrop-blur-md">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9A84C] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C9A84C]"></span>
              </span>
              <span className="text-xs sm:text-sm font-semibold tracking-wide uppercase text-[#E8D4A0]">
                Licensed Financial Professionals & Wealth Strategies
              </span>
              <Sparkles className="w-4 h-4 text-[#C9A84C]" />
            </div>

            {/* Headline with Brand Gold Metallic Text Gradient */}
            <div className="space-y-3">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-playfair tracking-tight leading-[1.15]">
                Build Lasting Wealth.<br />
                <span className="text-gradient-gold">Grow & Stay Rich</span> Forever.
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 font-opensans leading-relaxed max-w-2xl pt-2">
                Empowering families and business leaders with custom tax-advantaged retirement solutions, 0% market downside protection, and resilient estate legacy architecture.
              </p>
            </div>

            {/* Feature Bullet Points */}
            <div className="grid sm:grid-cols-2 gap-3 pt-1 text-sm text-slate-200">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0" />
                <span>Guaranteed 0% Market Downside Floor</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0" />
                <span>100% Tax-Free Retirement Growth</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0" />
                <span>Custom Financial Needs Analysis (FNA)</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0" />
                <span>Comprehensive Living Benefits Protection</span>
              </div>
            </div>

            {/* Call to Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <button
                onClick={() => scrollToSection("#lead-magnet")}
                className="bg-gradient-gold-cta text-[#070E1B] px-8 py-4 rounded-xl font-bold text-base shadow-xl flex items-center justify-center gap-3 group transition-all duration-300"
              >
                <Download className="w-5 h-5 text-[#070E1B] group-hover:-translate-y-0.5 transition-transform" />
                <span>Get Free Wealth Guide</span>
                <ArrowRight className="w-4 h-4 text-[#070E1B] group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => scrollToSection("#booking")}
                className="bg-[#162236]/80 hover:bg-[#1E3A5F] text-white border border-[#C9A84C]/40 hover:border-[#C9A84C] px-7 py-4 rounded-xl font-semibold text-base shadow-lg backdrop-blur-md flex items-center justify-center gap-2.5 transition-all duration-300"
              >
                <Calendar className="w-5 h-5 text-[#C9A84C]" />
                <span>Schedule Free Consultation</span>
              </button>
            </div>

            {/* Trust Bar & Social Proof */}
            <div className="pt-6 border-t border-slate-800/80 flex flex-wrap items-center gap-6 text-slate-300 text-xs sm:text-sm">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2 overflow-hidden">
                  <img className="inline-block h-9 w-9 rounded-full ring-2 ring-[#070E1B] object-cover" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&h=120&q=80" alt="Client" />
                  <img className="inline-block h-9 w-9 rounded-full ring-2 ring-[#070E1B] object-cover" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&h=120&q=80" alt="Client" />
                  <img className="inline-block h-9 w-9 rounded-full ring-2 ring-[#070E1B] object-cover" src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=120&h=120&q=80" alt="Client" />
                </div>
                <div>
                  <div className="flex items-center text-[#C9A84C]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                    <span className="ml-1.5 font-bold text-white text-xs">5.0</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5">Trusted by <strong className="text-white">500+</strong> Families</p>
                </div>
              </div>

              <div className="h-8 w-px bg-slate-800 hidden sm:block" />

              <div className="flex items-center gap-2 text-slate-300">
                <ShieldCheck className="w-5 h-5 text-[#C9A84C]" />
                <span>Fiduciary Standard • Licensed Advisor</span>
              </div>
            </div>

          </div>

          {/* ══ RIGHT COLUMN: Executive Leadership & Authority Hero Card ═════════ */}
          <div className="lg:col-span-5 relative mt-6 lg:mt-0">

            {/* Executive Frame Container */}
            <div className="relative rounded-3xl overflow-hidden border border-[#C9A84C]/40 shadow-[0_0_50px_rgba(201,168,76,0.2)] bg-[#162236]/90 backdrop-blur-xl group">

              {/* Executive Image */}
              <div className="relative aspect-[3/4] sm:aspect-[4/5] lg:aspect-[3/4] w-full overflow-hidden">
                <img
                  src="/images/priyanka-office.jpg"
                  alt="Priyanka Karn - Lead Wealth Specialist & Founder"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                />

                {/* Subtle Luxury Gradient Vignette Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070E1B] via-[#070E1B]/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#070E1B]/40 via-transparent to-transparent" />
              </div>

              {/* Bottom Overlaid Executive Bio Badge */}
              <div className="absolute bottom-0 inset-x-0 p-6 z-10 space-y-2.5 bg-[#070E1B]/85 backdrop-blur-xl border-t border-[#C9A84C]/30">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold font-playfair text-white tracking-wide">Priyanka Karn</h3>
                    <p className="text-xs font-semibold text-[#E8D4A0] tracking-wider uppercase">Lead Wealth Strategist & Licensed Specialist</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-[#C9A84C]/20 text-[#E8D4A0] border border-[#C9A84C]/40">
                    State Licensed
                  </span>
                </div>

                <div className="pt-2 border-t border-slate-700/60 flex items-center justify-between text-xs text-slate-300">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#C9A84C]" />
                    <span>Fiduciary Standard Advisory</span>
                  </div>

                </div>
              </div>

            </div>

            {/* Floating Live Metric Snapshot Badge */}
            <div className="hidden sm:flex items-center gap-3 absolute -bottom-6 -left-6 z-20 bg-[#0D1B2A]/95 border border-[#C9A84C]/40 p-4 rounded-2xl shadow-2xl backdrop-blur-xl animate-float-slow max-w-xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Tax-Free Wealth Positioned</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-sm font-extrabold text-emerald-400">$50M+ Positioned</span>
                  <span className="text-[10px] text-slate-400">• Zero Market Loss</span>
                </div>
              </div>
            </div>

            {/* Floating Security Badge */}
            <div className="hidden sm:flex items-center gap-2 absolute -top-4 -right-4 z-20 bg-[#162236]/95 border border-[#C9A84C]/40 px-4 py-2.5 rounded-2xl shadow-xl backdrop-blur-md">
              <Award className="w-4 h-4 text-[#C9A84C]" />
              <span className="text-xs font-bold text-white">100% Tax-Exempt Death Benefit</span>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
